import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client'; // Pour futures mises à jour
import { useTranslation } from 'react-i18next'; // Importer

// Définir une interface plus précise si possible, sinon utiliser `any` pour l'instant
export interface ReportedContentItem {
  id: string;
  contentType: string; // 'message' | 'profile' | 'review' etc.
  contentId: string; // ID du contenu signalé
  reportedBy: string; // ID de l'utilisateur signalant
  reporterName?: string; // Nom de l'utilisateur signalant (dénormalisé?)
  targetUser?: string; // ID de l'utilisateur ciblé
  targetName?: string; // Nom de l'utilisateur ciblé (dénormalisé?)
  reason: string;
  details?: string;
  createdAt: Date;
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed';
}

// Données mockées
const MOCK_REPORTED_CONTENT: ReportedContentItem[] = [
  {
    id: '1',
    contentType: 'message',
    contentId: 'm123',
    reportedBy: '201',
    reporterName: 'John D.',
    targetUser: '101',
    targetName: 'Sophia Rose',
    reason: 'Inappropriate content',
    details: 'The message contains threatening language',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    status: 'pending',
  },
  {
    id: '2',
    contentType: 'profile',
    contentId: '3',
    reportedBy: '202',
    reporterName: 'Michael S.',
    targetUser: '103',
    targetName: 'Emma Laurent',
    reason: 'Misrepresentation',
    details: 'Profile photos do not match the person',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
    status: 'pending',
  },
  {
    id: '3',
    contentType: 'review',
    contentId: 'r456',
    reportedBy: '102',
    reporterName: 'Mia Milano',
    targetUser: '201',
    targetName: 'John D.',
    reason: 'False information',
    details: 'The review contains false claims about services',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12),
    status: 'investigating',
  },
];

export const useReportedContent = () => {
  const [reports, setReports] = useState<ReportedContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation(); // Obtenir t

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: Remplacer par appel API réel (ex: supabase.from('reports').select('*'))
      await new Promise(resolve => setTimeout(resolve, 600)); // Simuler délai
      setReports(MOCK_REPORTED_CONTENT);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch reported content';
      console.error('Error fetching reports:', err);
      setError(errorMessage);
      toast({ title: t('common.error') || 'Error', description: t('admin.error_loading_reports') || errorMessage, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, t]); // Ajouter t

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const updateReportStatus = useCallback(async (reportId: string, newStatus: 'pending' | 'investigating' | 'resolved' | 'dismissed') => {
    // TODO: Implémenter l'appel API réel (ex: supabase.from('reports').update({ status: newStatus }).eq('id', reportId))
    console.log(`Updating report ${reportId} status to ${newStatus}`);
    setReports(currentReports =>
      currentReports.map(report =>
        report.id === reportId ? { ...report, status: newStatus } : report
      )
    );
    toast({ 
      title: t('common.success') || 'Success', 
      description: t('admin.report_status_updated', { reportId, status: newStatus }) || `Report ${reportId} status updated to ${newStatus}.` 
    });
    return true; // Simuler succès
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, t]); // Ajouter t

  return {
    reports,
    isLoading,
    error,
    fetchReports,
    updateReportStatus,
  };
}; 